import { useSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { Tab, Tabs, alpha } from '@mui/material';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { DocumentType } from 'src/services/files/file.types';
import candidate_scan from 'src/assets/lottie/candidate.json';
import { generateAsyncResumeImportError } from 'src/services/files/file.utils';
import {
  useUploadFileMutation,
  useCreateDocumentMutation,
} from 'src/services/files/file.service';
import { useGetApplicationByIdQuery, useImportApplicationProfileMutation } from 'src/services/applications/applications.service';

import { Upload } from 'src/components/upload';
import { useSettingsContext } from 'src/components/settings';
import AnimationContainer from 'src/components/animation/container';
import UploadItemCard from 'src/components/upload/upload-item-card';

import { ProfileDocumentSelector } from 'src/sections/profile/documents/profile-document-selector';

import { ConfirmDialog } from '../custom-dialog';

type Props = {
  applicationId: string;
  onSuccess: VoidFunction;
  onError?: (error: string) => void;
  showWarning?: boolean;
};

enum TabValue {
  UPLOAD = 'upload',
  SELECT = 'select',
}

export default function ApplicationProfileResumeUploader({
  applicationId,
  onSuccess,
  onError,
  showWarning = false,
}: Props) {
  const { t } = useTranslate();

  const settings = useSettingsContext();

  const { enqueueSnackbar } = useSnackbar();

  const authContext = useAuthContext();

  const confirm = useBoolean();

  const { currentData: application, isLoading } = useGetApplicationByIdQuery(
    applicationId as string,
    {
      skip: !applicationId,
    }
  );

  const [importProfile, { isLoading: importingResume }] = useImportApplicationProfileMutation();

  const [uploadFile, { isLoading: uploadingFile }] = useUploadFileMutation();

  const [createDocument, { isLoading: creatingDocument }] = useCreateDocumentMutation();

  const [file, setFile] = useState<File>();
  const [selectedTab, setSelectedTab] = useState<TabValue>(TabValue.UPLOAD);
  const [selectedDocument, setSelectedDocument] = useState<string>();

  const handleDocumentDrop = useCallback(
    async (documents: File[]) => {
      setFile(documents[0]);
    },
    [setFile]
  );

  const updateTab = useCallback((newValue: TabValue) => {
    setSelectedTab(newValue);
    setFile(undefined);
    setSelectedDocument(undefined);
  }, [setFile, setSelectedTab, setSelectedDocument]);

  const handleDocumentProcess = useCallback(async () => {
    confirm.onFalse();

    try {

      let documentId: string | undefined;
      if (selectedTab === TabValue.SELECT) {

        if (!selectedDocument) {
          enqueueSnackbar(t('errors.default_error'), {
            variant: 'error',
          });
          return;
        }

        documentId = selectedDocument;
      }
      else if (selectedTab === TabValue.UPLOAD) {

        if (!file) {
          enqueueSnackbar(t('errors.default_error'), {
            variant: 'error',
          });
          return;
        }

        const uploadedFile = await uploadFile({
          file: file as File,
          type: 'document' as any,
        }).unwrap();

        const document = await createDocument({
          organization_id: authContext.organization.id,
          document: {
            file_id: uploadedFile.id,
            name: (file as File).name,
            type: DocumentType.RESUME,
          },
        }).unwrap();

        documentId = document.id;
      }

      await importProfile({
        applicationId,
        documentId: documentId as string
      }).unwrap();

      enqueueSnackbar(t('applications.candidate_resume_importer.async_api.success'), {
        variant: 'success',
      });

      onSuccess();
    } catch (e) {
      setSelectedDocument(undefined);
      const errorMsg = generateAsyncResumeImportError(e);
      onError?.(errorMsg);
      enqueueSnackbar(errorMsg, {
        variant: 'error',
      });
      setFile(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authContext.organization.id,
    createDocument,
    enqueueSnackbar,
    file,
    importProfile,
    onSuccess,
    uploadFile,
    selectedTab,
    selectedDocument,
    applicationId,
    onError,
    t,
  ]);

  return (
    <Stack gap={2} width="100%">
      <Box>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => updateTab(newValue)}
          sx={{
            px: 2.5,
            mb: { xs: 1, sm: 'unset' },
            boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
          }}
        >
          <Tab
            key={TabValue.UPLOAD}
            value={TabValue.UPLOAD}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('components.resume_importer.tabs.upload')}
              </Typography>
            }
          />
          <Tab
            key={TabValue.SELECT}
            value={TabValue.SELECT}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('components.resume_importer.tabs.select')}
              </Typography>
            }
          />
        </Tabs>
      </Box>

      <Box width="100%" maxHeight='80vh' sx={{ mb: 2 }}>
        {!importingResume ? (
          <Box>
            {
              selectedTab === TabValue.UPLOAD && (
                <Stack direction="column" gap={1}>
                  <Upload
                    accept={{ '.pdf': [], '.docx': [], '.doc': [] }}
                    onDrop={handleDocumentDrop}
                    helperText={t('components.resume_importer.helper_text')}
                  />

                  {file && (
                    <UploadItemCard
                      title={(file as any).path}
                      onDeleteFile={() => setFile(undefined)}
                      uploading={uploadingFile}
                    />
                  )}
                </Stack>
              )
            }

            {
              selectedTab === TabValue.SELECT && (
                <Box sx={{ mt: 1 }}>
                  <ProfileDocumentSelector
                    enableUpload={false}
                    profileId={application?.attached_profile?.id as string}
                    onSelect={(type, document) => {
                      setSelectedDocument(document?.document.id);
                      setFile(undefined);
                    }}
                    type={DocumentType.RESUME}
                    label={t('applications.candidate_resume_importer.modal.select_document')}
                  />
                </Box>
              )
            }

          </Box>
        ) : (
          <Box display="flex" flexDirection="column" justifyContent="center" textAlign="center" alignItems="center">
            <AnimationContainer height={320} width={410} json={candidate_scan} loop />
            <Typography
              fontSize={13}
              className={settings.themeMode === 'light' ? 'text-gray-600' : 'text-gray-100'}
            >
              {t('applications.candidate_resume_importer.modal.loading')}
            </Typography>
          </Box>
        )}
      </Box>

      <Box>
        {selectedTab === TabValue.UPLOAD && (
          <LoadingButton
            disabled={!file}
            variant="contained"
            fullWidth
            loading={creatingDocument || uploadingFile || importingResume}
            onClick={showWarning ? confirm.onTrue : handleDocumentProcess}
          >
            {t('common.upload')}
          </LoadingButton>
        )}

        {selectedTab === TabValue.SELECT && (
          <LoadingButton
            disabled={!selectedDocument}
            variant="contained"
            fullWidth
            loading={importingResume}
            onClick={showWarning ? confirm.onTrue : handleDocumentProcess}
          >
            {t('common.select')}
          </LoadingButton>
        )}
      </Box>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('profile.importer.confirm.title')}
        content={t('applications.candidate_resume_importer.modal.confirm')}
        action={
          <Button variant="contained" color="error" onClick={handleDocumentProcess}>
            {t('common.confirm')}
          </Button>
        }
      />
    </Stack>
  );
}
